import { useState, useEffect, useCallback } from 'react';
import { US_STATES } from '../constants/location-data';
import { getRateLimitStatus, recordCalculation } from '../utils/rateLimiter';

export const useROICalculation = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rateLimitStatus, setRateLimitStatus] = useState(() => getRateLimitStatus());

  const getLocationDisplay = () => {
    const params = new URLSearchParams(window.location.search);
    const targetLocation = params.get('targetLocation');
    const state = params.get('state');
    const city = params.get('city');

    if (city && state) {
      const stateName = US_STATES.find(s => s.code === state)?.name || state;
      return `${city}, ${stateName}`;
    } else if (state) {
      const stateName = US_STATES.find(s => s.code === state)?.name || state;
      return stateName;
    }
    return targetLocation || 'United States';
  };

  const parseURLParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      businessName: params.get('businessName'),
      website: params.get('website'),
      businessDescription: params.get('businessDescription'),
      monthlyBudget: params.get('monthlyBudget'),
      targetLocation: params.get('targetLocation'),
      averageOrderValue: params.get('averageOrderValue'),
      state: params.get('state'),
      city: params.get('city'),
      locations: params.get('locations') // All locations as JSON string
    };
  };

  const validateParams = (params) => {
    const { businessDescription, monthlyBudget, averageOrderValue } = params;
    
    if (!businessDescription || !monthlyBudget || !averageOrderValue) {
      return {
        isValid: false,
        error: 'Missing required parameters. Please fill out all required fields in the calculator.'
      };
    }

    return { isValid: true };
  };

  const buildLocationsArray = (params) => {
    // First try to get all locations from JSON if available (new method)
    const locationsParam = params.locations;
    if (locationsParam) {
      try {
        const locationsArray = JSON.parse(locationsParam);
        if (Array.isArray(locationsArray) && locationsArray.length > 0) {
          console.log('✅ Using all locations from form:', locationsArray);
          return locationsArray;
        }
      } catch (error) {
        console.warn('Failed to parse locations JSON, falling back to single location');
      }
    }

    // Fallback to single location method (backwards compatibility)
    const { state, city } = params;
    
    return [{
      state: state || 'US',
      stateName: state ? US_STATES.find(s => s.code === state)?.name || state : 'United States',
      customLocation: city || null,
      displayName: city ? `${city}, ${state}` : (state ? US_STATES.find(s => s.code === state)?.name || state : 'United States'),
      type: city ? 'city' : (state ? 'state' : 'country')
    }];
  };

  const updateRateLimitStatus = useCallback(() => {
    const status = getRateLimitStatus();
    setRateLimitStatus(status);
    return status;
  }, []);

  const calculateROI = useCallback(async () => {
    try {
      // Check rate limit before making request
      const currentStatus = updateRateLimitStatus();
      
      if (!currentStatus.canCalculate) {
        const resetInfo = currentStatus.timeUntilReset 
          ? ` Please try again in ${currentStatus.timeUntilReset}.`
          : ' Please try again later.';
        
        setError(`You've reached the limit of 3 calculations per 24 hours.${resetInfo}`);
        return;
      }

      setLoading(true);
      setError(null);

      // Record the calculation attempt
      recordCalculation();
      updateRateLimitStatus();

      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? 'https://metricslab.onrender.com' 
        : '';

      const params = parseURLParams();
      
      console.log('🔍 URL Search:', window.location.search);
      console.log('🔍 Parsed parameters:', params);

      const validation = validateParams(params);
      if (!validation.isValid) {
        console.log('❌ Validation failed:', validation.error);
        setError(validation.error);
        setLoading(false);
        return;
      }

      console.log('✅ Validation passed, making API call...');

      const locations = buildLocationsArray(params);

      const payload = {
        businessName: params.businessName || '',
        businessWebsite: params.website || '',
        businessDescription: params.businessDescription,
        monthlyBudget: parseFloat(params.monthlyBudget),
        locations,
        averageOrderValue: parseFloat(params.averageOrderValue),
      };

      console.log('🚀 Calling ROI API with data:', payload);

      const response = await fetch(`${API_BASE_URL}/api/roi/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ ROI API Response:', data);
      
      setResults(data);
      return data;

    } catch (err) {
      console.error('❌ ROI Calculation Error:', err);
      let errorMessage = 'Failed to calculate ROI. ';
      
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage += 'Please check your internet connection and try again.';
      } else if (err.message.includes('Server error: 5')) {
        errorMessage += 'Our servers are temporarily unavailable. Please try again in a few minutes.';
      } else {
        errorMessage += err.message || 'Please try again later.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [updateRateLimitStatus]);

  useEffect(() => {
    calculateROI();
  }, [calculateROI]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const resetResults = useCallback(() => {
    setResults(null);
    setError(null);
  }, []);

  return {
    results,
    loading,
    error,
    getLocationDisplay,
    retry: calculateROI,
    resetError,
    resetResults,
    rateLimitStatus,
    updateRateLimitStatus
  };
}; 